import Select from 'react-select';
import { getOptionByValue, getSelectMultiValueFromOptions, getSelectOptions, isArray, isMultiValue } from './utils';
import { BaseProps, Option, SelectProps, SelectPropsToOmit } from './types';
import { useMemo } from 'react';
import { useInnerComponentsClassNames } from './hooks';

type Props<T> = Omit<SelectProps<T>, SelectPropsToOmit> &
    BaseProps & {
        value?: T | T[];
        options: Option<T>[];
        onChange: (value: T | T[] | undefined) => void;
    };

const PolicySelectField = <T,>({
    disabled = false,
    clearable = false,
    searchable = false,
    multi = false,
    backspaceRemoves = false,
    value,
    options,
    onChange,
    className = '',
    ...selectProps
}: Props<T>) => {
    const { innerComponentsClassNames } = useInnerComponentsClassNames<T>(className);
    const selectOptions = useMemo(() => getSelectOptions(options), [options]);

    const getValue = (value?: T | T[]): SelectProps<T>['value'] => {
        if (value === undefined) return;

        if (isArray(value)) {
            return getSelectMultiValueFromOptions(value, selectOptions);
        }

        return getOptionByValue(value, selectOptions);
    };

    const handleChange: SelectProps<T>['onChange'] = (newValue) => {
        if (!newValue) {
            onChange(undefined);
        } else if (isMultiValue(newValue)) {
            onChange(newValue.map((option) => option.value));
        } else {
            onChange(newValue?.value);
        }
    };

    return (
        <Select
            className="flex-1"
            classNames={innerComponentsClassNames}
            isMulti={multi}
            isClearable={clearable}
            isSearchable={searchable}
            backspaceRemovesValue={backspaceRemoves}
            value={getValue(value)}
            options={selectOptions}
            onChange={handleChange}
            isDisabled={disabled}
            {...selectProps}
        />
    );
};

export default PolicySelectField;
